/**
 * /admin — unified admin hub. One authenticated door to every admin console.
 * Gated by the academy_token (admin/admin_role required server-side).
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Loader2, LogOut, ShieldCheck, ArrowUpRight,
  Package, Shirt, FileText, GraduationCap,
  Tag, MessageSquare, Sunrise, Map, Clapperboard, Compass, Gift,
  Video, RefreshCw, PlayCircle, Mail, CalendarClock, Users, Radio, Link2, Copy,
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';
import EnvBanner from '../components/EnvBanner';
import JitsiRoom from '../components/JitsiRoom';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });

const fmtDT = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (isNaN(d)) return '—';
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch { return '—'; }
};

const RecentActivityTool = () => {
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);

  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get('/academy/admin/activity', { headers: authHeaders() });
      setData(r.data);
    } catch (e) { toast.error('Could not load activity'); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const s = data?.summary;
  const accounts = data?.accounts || [];
  const chip = (label, val, color) => (
    <div className="bg-black/40 border border-gray-800 px-3 py-2 rounded" data-testid={`activity-stat-${label}`}>
      <div className="text-xl font-black" style={{ color }}>{val ?? '—'}</div>
      <div className="text-[9px] uppercase tracking-wider text-gray-500">{label.replace(/-/g, ' ')}</div>
    </div>
  );

  return (
    <div className="mt-6 border-2 border-teal-500/40 bg-teal-500/[0.04] p-5" data-testid="recent-activity-tool">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-teal-400" />
          <h3 className="text-sm font-black uppercase tracking-[0.2em] text-white">Recent Activity</h3>
        </div>
        <button onClick={load} disabled={busy} className="text-gray-500 hover:text-white transition-colors" data-testid="recent-activity-refresh" title="Refresh">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
        </button>
      </div>
      <p className="text-[11px] text-gray-500 mb-4">Every academy account with sign-up date and last-active time. <span className="text-gray-600">"Last Active" comes from the student-dashboard presence ping (there is no separate login log yet).</span></p>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4">
        {chip('total', s?.total, '#2DD4BF')}
        {chip('students', s?.students, '#38BDF8')}
        {chip('active-24h', s?.active_24h, '#34D399')}
        {chip('active-7d', s?.active_7d, '#A3E635')}
        {chip('new-7d', s?.new_7d, '#F59E0B')}
      </div>

      {accounts.length === 0 ? (
        <div className="text-xs text-gray-600 py-4 text-center border-2 border-dashed border-gray-800" data-testid="recent-activity-empty">No accounts yet.</div>
      ) : (
        <div className="max-h-96 overflow-y-auto border border-gray-800 rounded">
          <table className="w-full text-left" data-testid="recent-activity-table">
            <thead className="sticky top-0 bg-[#0d0d10]">
              <tr className="text-[9px] uppercase tracking-wider text-gray-500">
                <th className="px-3 py-2">Account</th>
                <th className="px-3 py-2">Role</th>
                <th className="px-3 py-2">Grade · State</th>
                <th className="px-3 py-2">Joined</th>
                <th className="px-3 py-2">Last Active</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((u, i) => (
                <tr key={u.id || i} className="border-t border-gray-900 text-xs" data-testid={`recent-activity-row-${i}`}>
                  <td className="px-3 py-2">
                    <div className="text-white font-medium truncate max-w-[220px]">{u.name || '—'}</div>
                    <div className="text-gray-500 font-mono text-[10px] truncate max-w-[220px]">{u.email}</div>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`text-[9px] font-black uppercase px-1.5 py-0.5 rounded ${u.role === 'teacher' ? 'bg-orange-500/20 text-orange-300' : u.role === 'student' ? 'bg-blue-500/20 text-blue-300' : 'bg-gray-700/40 text-gray-400'}`}>{u.role}</span>
                  </td>
                  <td className="px-3 py-2 text-gray-400">{u.grade_level ? `G${u.grade_level}` : '—'}{u.us_state ? ` · ${u.us_state}` : ''}</td>
                  <td className="px-3 py-2 text-gray-400">{fmtDT(u.created_at)}</td>
                  <td className="px-3 py-2"><span className={u.last_seen ? 'text-emerald-400' : 'text-gray-600'}>{fmtDT(u.last_seen)}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const GrantLchaimTool = () => {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const grant = async () => {
    if (!email.trim()) { toast.error('Enter an email'); return; }
    setBusy(true); setResult(null);
    try {
      const r = await api.post('/academy/admin/grant-lchaim', { email: email.trim() }, { headers: authHeaders() });
      setResult(r.data);
      if (r.data.success) toast.success(r.data.message); else toast.error(r.data.message);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Grant failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-8 border-2 border-emerald-500/40 bg-emerald-500/[0.04] p-5" data-testid="grant-lchaim-tool">
      <div className="flex items-center gap-2 mb-1"><Gift className="w-4 h-4 text-emerald-400" /><h3 className="text-sm font-black uppercase tracking-[0.2em]">Comp L'Chaim Access</h3></div>
      <p className="text-xs text-gray-500 mb-3">Grant a user free (uncharged) L'Chaim dashboard access by email — shows in their dashboard immediately.</p>
      <div className="flex flex-col sm:flex-row gap-2">
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && grant()} placeholder="user@email.com" className="flex-1 bg-black border-2 border-gray-800 px-3 py-2.5 text-white font-mono text-sm focus:border-emerald-500 outline-none" data-testid="grant-lchaim-email" />
        <button onClick={grant} disabled={busy} className="flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:bg-gray-700 text-black px-5 py-2.5 text-[10px] font-black uppercase tracking-[0.2em] transition-colors" data-testid="grant-lchaim-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gift className="w-4 h-4" />} Grant Free Access
        </button>
      </div>
      {result && <div className={`mt-2 text-xs font-bold ${result.success ? 'text-emerald-400' : 'text-orange-400'}`} data-testid="grant-lchaim-result">{result.message}</div>}
    </div>
  );
};

const ParentWelcomeTool = () => {
  const [email, setEmail] = useState('');
  const [parentName, setParentName] = useState('');
  const [studentName, setStudentName] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const send = async () => {
    if (!email.trim()) { toast.error('Enter a parent email'); return; }
    setBusy(true); setResult(null);
    try {
      const r = await api.post('/academy/parent-welcome/send', {
        mode: 'all',
        recipients: [{
          email: email.trim(),
          parent_name: parentName.trim(),
          student_name: studentName.trim() || 'your scholar',
        }],
      }, { headers: authHeaders() });
      setResult(r.data);
      if (r.data.success && r.data.sent) {
        toast.success(`Welcome email sent to ${email.trim()}`);
        setEmail(''); setParentName(''); setStudentName('');
      } else {
        toast.error('Send failed — check the address');
      }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Send failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-6 border-2 border-blue-500/40 bg-blue-500/[0.04] p-5" data-testid="parent-welcome-tool">
      <div className="flex items-center gap-2 mb-1"><Mail className="w-4 h-4 text-blue-400" /><h3 className="text-sm font-black uppercase tracking-[0.2em]">Send Parent Welcome Email</h3></div>
      <p className="text-xs text-gray-500 mb-3">Send the season Welcome + Orientation email (from margaret@prsvr.com) to any new registrant. Links point to the live Parent Welcome Hub.</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="parent@email.com" className="bg-black border-2 border-gray-800 px-3 py-2.5 text-white font-mono text-sm focus:border-blue-500 outline-none" data-testid="parent-welcome-email" />
        <input type="text" value={parentName} onChange={(e) => setParentName(e.target.value)} placeholder="Parent name (optional)" className="bg-black border-2 border-gray-800 px-3 py-2.5 text-white text-sm focus:border-blue-500 outline-none" data-testid="parent-welcome-parent-name" />
        <input type="text" value={studentName} onChange={(e) => setStudentName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && send()} placeholder="Student name (optional)" className="bg-black border-2 border-gray-800 px-3 py-2.5 text-white text-sm focus:border-blue-500 outline-none" data-testid="parent-welcome-student-name" />
      </div>
      <button onClick={send} disabled={busy} className="mt-2 flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-400 disabled:bg-gray-700 text-white px-5 py-2.5 text-[10px] font-black uppercase tracking-[0.2em] transition-colors" data-testid="parent-welcome-send-btn">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Send Welcome Email
      </button>
      {result && result.sent > 0 && <div className="mt-2 text-xs font-bold text-blue-400" data-testid="parent-welcome-result">Sent {result.sent} email(s).</div>}
    </div>
  );
};

const ParentLinkTool = () => {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState('');
  const send = async () => {
    if (!email.trim()) { toast.error('Enter a parent email'); return; }
    setBusy(true); setLink('');
    try {
      const r = await api.post('/academy/parent/link/send', { email: email.trim() }, { headers: authHeaders() });
      setLink(r.data.link);
      toast.success(r.data.sent ? `Progress link emailed to ${email.trim()}` : 'Link generated (email not sent — check Resend key)');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not generate link'); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-6 border-2 border-indigo-500/40 bg-indigo-500/[0.04] p-5" data-testid="parent-link-tool">
      <div className="flex items-center gap-2 mb-1"><Link2 className="w-4 h-4 text-indigo-400" /><h3 className="text-sm font-black uppercase tracking-[0.2em]">Send Family Progress Link</h3></div>
      <p className="text-xs text-gray-500 mb-3">Generate a passwordless magic link to the parent's gamified Family Hub (Scholar Card, quests, mastery, materials). Emails it from margaret@prsvr.com. Requires an enrolled scholar with this parent email.</p>
      <div className="flex flex-col sm:flex-row gap-2">
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && send()} placeholder="parent@email.com" className="flex-1 bg-black border-2 border-gray-800 px-3 py-2.5 text-white font-mono text-sm focus:border-indigo-500 outline-none" data-testid="parent-link-email" />
        <button onClick={send} disabled={busy} className="flex items-center justify-center gap-2 bg-indigo-500 hover:bg-indigo-400 disabled:bg-gray-700 text-white px-5 py-2.5 text-[10px] font-black uppercase tracking-[0.2em] transition-colors" data-testid="parent-link-send-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Send Link
        </button>
      </div>
      {link && (
        <div className="mt-3 flex items-center gap-2 bg-black/40 border border-gray-800 px-3 py-2" data-testid="parent-link-result">
          <span className="text-xs text-gray-400 font-mono truncate flex-1">{link}</span>
          <button onClick={() => { navigator.clipboard.writeText(link); toast.success('Copied'); }} className="text-indigo-400 hover:text-indigo-300 shrink-0" data-testid="parent-link-copy"><Copy className="w-4 h-4" /></button>
        </div>
      )}
    </div>
  );
};

const LIVE_GROUPS = [['g5', 'Grade 5'], ['g67', 'Grades 6-7'], ['g8', 'Grade 8']];

const LiveSessionTool = () => {
  const [title, setTitle] = useState('School-Wide Live Session');
  const [scope, setScope] = useState('all');
  const [gradeGroup, setGradeGroup] = useState('g5');
  const [students, setStudents] = useState([]);
  const [picked, setPicked] = useState({});
  const [sessions, setSessions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [hosting, setHosting] = useState(null);
  const [domain, setDomain] = useState('meet.jit.si');

  const loadSessions = async () => {
    try { const r = await api.get('/academy/live/sessions', { headers: authHeaders() }); setSessions(r.data.sessions || []); if (r.data.domain) setDomain(r.data.domain); } catch (e) { /* noop */ }
  };
  useEffect(() => { loadSessions(); }, []);
  useEffect(() => {
    if (scope === 'student' && students.length === 0) {
      api.get('/academy/admin/users', { headers: authHeaders() }).then((r) => setStudents((r.data.users || []).filter((u) => u.role !== 'teacher'))).catch(() => {});
    }
  }, [scope]); // eslint-disable-line

  const start = async () => {
    setBusy(true);
    try {
      const body = { title, scope };
      if (scope === 'grade') body.grade_group = gradeGroup;
      if (scope === 'student') body.student_ids = Object.keys(picked).filter((k) => picked[k]);
      const r = await api.post('/academy/live/start', body, { headers: authHeaders() });
      toast.success('Session started — targeted students can now join.');
      setHosting(r.data.session);
      loadSessions();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to start'); }
    finally { setBusy(false); }
  };
  const end = async (sid) => {
    try { await api.post(`/academy/live/${sid}/end`, {}, { headers: authHeaders() }); toast.success('Session ended'); if (hosting?.id === sid) setHosting(null); loadSessions(); } catch (e) { toast.error('Failed'); }
  };

  return (
    <div className="mt-6 border-2 border-red-500/40 bg-red-500/[0.04] p-5" data-testid="live-session-tool">
      <div className="flex items-center gap-2 mb-1"><Radio className="w-4 h-4 text-red-400" /><h3 className="text-sm font-black uppercase tracking-[0.2em]">Host a Live Session</h3></div>
      <p className="text-xs text-gray-500 mb-3">Broadcast a live video meeting to everyone, a grade group, or specific students. Targeted students get a "Join" prompt on their dashboard. The call floats so you can keep using the dashboard.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Session title" className="bg-black border-2 border-gray-800 px-3 py-2.5 text-sm focus:border-red-500 outline-none" data-testid="live-title" />
        <select value={scope} onChange={(e) => setScope(e.target.value)} className="bg-black border-2 border-gray-800 px-3 py-2.5 text-sm focus:border-red-500 outline-none" data-testid="live-scope">
          <option value="all">Everyone (all students)</option>
          <option value="grade">A grade group</option>
          <option value="student">Specific students</option>
        </select>
      </div>
      {scope === 'grade' && (
        <select value={gradeGroup} onChange={(e) => setGradeGroup(e.target.value)} className="mt-2 w-full bg-black border-2 border-gray-800 px-3 py-2.5 text-sm focus:border-red-500 outline-none" data-testid="live-grade">
          {LIVE_GROUPS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      )}
      {scope === 'student' && (
        <div className="mt-2 max-h-40 overflow-y-auto border-2 border-gray-800 p-2" data-testid="live-student-list">
          {students.length === 0 ? <div className="text-xs text-gray-600 p-2">Loading students…</div> : students.map((u) => (
            <label key={u.id} className="flex items-center gap-2 py-1 text-xs text-gray-300">
              <input type="checkbox" checked={!!picked[u.id]} onChange={(e) => setPicked((p) => ({ ...p, [u.id]: e.target.checked }))} data-testid={`live-pick-${u.id}`} />
              {u.name} <span className="text-gray-600">· {u.email}{u.grade_level ? ` · G${u.grade_level}` : ''}</span>
            </label>
          ))}
        </div>
      )}
      <button onClick={start} disabled={busy} className="mt-3 flex items-center justify-center gap-2 bg-red-500 hover:bg-red-400 disabled:bg-gray-700 text-white px-5 py-2.5 text-[10px] font-black uppercase tracking-[0.2em] transition-colors" data-testid="live-start-btn">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Radio className="w-4 h-4" />} Start Live Session
      </button>

      {sessions.length > 0 && (
        <div className="mt-4 space-y-2" data-testid="live-active-list">
          <div className="text-[10px] font-black uppercase tracking-widest text-gray-500">Active sessions</div>
          {sessions.map((s) => (
            <div key={s.id} className="flex items-center justify-between border border-red-500/30 bg-black/40 px-3 py-2" data-testid={`live-active-${s.id}`}>
              <div className="min-w-0"><div className="text-xs font-bold text-white truncate">{s.title}</div><div className="text-[10px] text-gray-500 uppercase">{s.scope}{s.grade_group ? ` · ${s.grade_group}` : ''}</div></div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button onClick={() => setHosting(s)} className="text-[10px] font-black uppercase tracking-wider text-red-400 hover:text-red-300" data-testid={`live-host-${s.id}`}>Join as Host</button>
                <button onClick={() => end(s.id)} className="text-[10px] font-black uppercase tracking-wider text-gray-500 hover:text-white" data-testid={`live-end-${s.id}`}>End</button>
              </div>
            </div>
          ))}
        </div>
      )}
      {hosting && <JitsiRoom room={hosting.room} displayName="Head Coach" title={hosting.title} domain={domain} onClose={() => setHosting(null)} />}
    </div>
  );
};

const GROUP_LABELS = { g5: 'Grade 5', g67: 'Grades 6-7', g8: 'Grade 8' };

const OrientationBookingsTool = () => {
  const [bookings, setBookings] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get('/academy/orientation/admin/bookings', { headers: authHeaders() });
      setBookings(r.data.bookings || []);
    } catch (e) { toast.error('Could not load bookings'); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);
  return (
    <div className="mt-6 border-2 border-amber-500/40 bg-amber-500/[0.04] p-5" data-testid="orientation-bookings-tool">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2"><CalendarClock className="w-4 h-4 text-amber-400" /><h3 className="text-sm font-black uppercase tracking-[0.2em]">Orientation Bookings <span className="text-amber-400">({bookings.length})</span></h3></div>
        <button onClick={load} disabled={busy} className="text-gray-500 hover:text-white transition-colors" data-testid="orientation-bookings-refresh" title="Refresh">
          <RefreshCw className={`w-4 h-4 ${busy ? 'animate-spin' : ''}`} />
        </button>
      </div>
      <p className="text-xs text-gray-500 mb-3">Families who reserved a Family Orientation window (Aug 24–27). You also get an email each time a family books.</p>
      {bookings.length === 0 ? (
        <div className="text-xs text-gray-600 py-4 text-center border-2 border-dashed border-gray-800" data-testid="orientation-bookings-empty">No orientation bookings yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="text-gray-500 uppercase tracking-widest text-[9px] border-b border-gray-800">
                <th className="py-2 pr-3">Day</th><th className="py-2 pr-3">Time</th><th className="py-2 pr-3">Family</th><th className="py-2 pr-3">Student</th><th className="py-2 pr-3">Email</th><th className="py-2">Phone</th>
              </tr>
            </thead>
            <tbody>
              {bookings.map((b, i) => (
                <tr key={i} className="border-b border-gray-900" data-testid={`orientation-booking-row-${i}`}>
                  <td className="py-2 pr-3 font-bold text-white whitespace-nowrap">{b.day_label}</td>
                  <td className="py-2 pr-3 text-amber-400 font-bold whitespace-nowrap">{b.slot_label}</td>
                  <td className="py-2 pr-3">{b.family_name}</td>
                  <td className="py-2 pr-3">{b.student_name}</td>
                  <td className="py-2 pr-3"><a href={`mailto:${b.parent_email}`} className="text-blue-400 hover:underline">{b.parent_email}</a></td>
                  <td className="py-2 text-gray-400">{b.phone || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const CoachBriefingTool = () => {
  const [videos, setVideos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [preview, setPreview] = useState(null);

  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get('/academy/heygen/daily-status', { headers: authHeaders() });
      setVideos(r.data.videos || []);
    } catch (e) { /* silent */ }
    finally { setBusy(false); }
  };

  useEffect(() => { load(); }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      await api.post('/academy/heygen/generate-daily', {}, { headers: authHeaders() });
      toast.success("Generating today's coach briefings — renders in ~1-3 min.");
      // Poll status a few times to reflect completion
      let tries = 0;
      const timer = setInterval(async () => {
        tries += 1;
        await load();
        if (tries >= 12) { clearInterval(timer); setGenerating(false); }
      }, 20000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to start generation');
      setGenerating(false);
    }
  };

  const byGroup = (g) => videos.find((v) => v.grade_group === g);

  return (
    <div className="mt-6 border-2 border-orange-500/40 bg-orange-500/[0.04] p-5" data-testid="coach-briefing-tool">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2"><Video className="w-4 h-4 text-orange-400" /><h3 className="text-sm font-black uppercase tracking-[0.2em]">AI Head Coach Briefings</h3></div>
        <button onClick={load} disabled={busy} className="text-gray-500 hover:text-white transition-colors" data-testid="coach-briefing-refresh" title="Refresh status">
          <RefreshCw className={`w-4 h-4 ${busy ? 'animate-spin' : ''}`} />
        </button>
      </div>
      <p className="text-xs text-gray-500 mb-3">Generate today's personalized 15-second Head Coach videos per grade group on demand (auto-runs 6:15 AM PT).</p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
        {['g5', 'g67', 'g8'].map((g) => {
          const v = byGroup(g);
          const status = v?.status || 'none';
          const color = status === 'completed' ? 'text-emerald-400 border-emerald-500/40' : status === 'processing' ? 'text-amber-400 border-amber-500/40' : status === 'failed' ? 'text-red-400 border-red-500/40' : 'text-gray-500 border-gray-800';
          return (
            <div key={g} className={`border-2 ${color} bg-black/40 p-3`} data-testid={`coach-briefing-${g}`}>
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-black uppercase tracking-widest text-white">{GROUP_LABELS[g]}</span>
                <span className="text-[9px] font-bold uppercase tracking-wider">{status}</span>
              </div>
              {v?.focus && <p className="text-[10px] text-gray-500 mt-1 truncate" title={v.focus}>{v.focus}</p>}
              {v?.video_url && (
                <button onClick={() => setPreview(v)} className="mt-2 flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-orange-400 hover:text-orange-300" data-testid={`coach-briefing-preview-${g}`}>
                  <PlayCircle className="w-3.5 h-3.5" /> Preview
                </button>
              )}
            </div>
          );
        })}
      </div>

      <button onClick={generate} disabled={generating} className="flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-400 disabled:bg-gray-700 text-black px-5 py-2.5 text-[10px] font-black uppercase tracking-[0.2em] transition-colors" data-testid="coach-briefing-generate-btn">
        {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Video className="w-4 h-4" />} Generate Today's Briefings
      </button>

      {preview && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setPreview(null)} data-testid="coach-briefing-modal">
          <div className="w-full max-w-lg bg-zinc-950 border-2 border-orange-500 p-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-black uppercase tracking-widest text-orange-400">{GROUP_LABELS[preview.grade_group]} · Coach Briefing</span>
              <button onClick={() => setPreview(null)} className="text-gray-500 hover:text-white text-xs font-black" data-testid="coach-briefing-modal-close">✕</button>
            </div>
            <video src={preview.video_url} poster={preview.poster_url || undefined} controls autoPlay className="w-full aspect-video bg-black" data-testid="coach-briefing-modal-video" />
            {preview.script && <p className="text-[11px] text-gray-400 mt-2 leading-relaxed">{preview.script}</p>}
          </div>
        </div>
      )}
    </div>
  );
};

const SECTIONS = [
  {
    group: 'Store & Fulfillment',
    items: [
      { title: 'Order Tracker', path: '/admin/orders', icon: Package, desc: 'Create & track PRSVR / DILA orders, advance cure stages, copy or resend client tracking links.' },
      { title: 'BYWE Team Merch', path: '/admin/team-orders', icon: Shirt, desc: 'Team merch reservations, paid/pending revenue, status updates and CSV export.' },
      { title: 'Client Briefs', path: '/admin-invoices', icon: FileText, desc: 'Client brief dashboard — intake, invoices and project detail.' },
    ],
  },
  {
    group: 'Academy',
    items: [
      { title: 'Academy Portal', path: '/academy/admin-portal', icon: GraduationCap, desc: 'Leads, enrolled students, attendance and academy management.' },
      { title: 'Student & Parent Directory', path: '/admin/users', icon: Users, desc: 'View every account, edit info, reset passwords, deactivate or delete accounts, with a full change log.' },
      { title: 'Game Control', path: '/teacher-dashboard', icon: Compass, desc: 'Daily AI game plan, live station rotation, exit-ticket mastery, and Summer Prep Plans per grade.' },
      { title: 'Morning Report', path: '/admin/morning-report', icon: Sunrise, desc: 'Daily enrollment & attendance snapshot.' },
    ],
  },
  {
    group: 'Marketing & Comms',
    items: [
      { title: 'Offers · Hook It Up', path: '/admin/offers', icon: Tag, desc: 'Manage promotional offers and discount hooks.' },
      { title: 'Communications', path: '/admin/communications', icon: MessageSquare, desc: 'Communications center — broadcasts and messaging.' },
    ],
  },
  {
    group: 'Content & Media',
    items: [
      { title: 'Road to Michigan', path: '/admin/run', icon: Map, desc: 'The Chila Run tracker — map stops, geocoding and video uploads.' },
      { title: 'HeyGen Videos', path: '/admin/heygen', icon: Clapperboard, desc: 'AI video / avatar script manager.' },
    ],
  },
];

const AdminHub = () => {
  const [loading, setLoading] = useState(true);
  const [authed, setAuthed] = useState(false);
  const [creds, setCreds] = useState({ email: '', password: '' });
  const [loggingIn, setLoggingIn] = useState(false);

  const verify = async () => {
    const token = localStorage.getItem('academy_token');
    if (!token) { setAuthed(false); setLoading(false); return; }
    try {
      await api.get('/v1/merch-hub/admin/teams', { headers: authHeaders() });
      setAuthed(true);
    } catch (e) {
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { verify(); }, []);

  const doLogin = async () => {
    if (!creds.email || !creds.password) { toast.error('Enter email and password'); return; }
    setLoggingIn(true);
    try {
      const res = await api.post('/academy/auth/login', creds);
      const token = res.data.token;
      if (!token) throw new Error('no token');
      localStorage.setItem('academy_token', token);
      setLoading(true);
      await verify();
      toast.success('Signed in');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Login failed');
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('academy_token');
    setAuthed(false);
    toast.success('Signed out');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (!authed) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-4">
        <div className="w-full max-w-[380px] border-2 border-gray-800 p-8" data-testid="admin-hub-login">
          <div className="text-[10px] font-bold tracking-[0.4em] text-orange-500 uppercase">PRSVR</div>
          <h1 className="text-2xl font-black uppercase tracking-tight mt-2">Admin Hub</h1>
          <p className="text-xs text-gray-500 mt-2 leading-relaxed">Sign in to access every admin console from one place.</p>
          <div className="mt-6 space-y-3">
            <input
              type="email"
              placeholder="Email"
              value={creds.email}
              onChange={(e) => setCreds({ ...creds, email: e.target.value })}
              className="w-full bg-black border-2 border-gray-800 px-3 py-3 text-white font-mono text-sm focus:border-orange-500 outline-none"
              data-testid="admin-hub-email"
            />
            <input
              type="password"
              placeholder="Password"
              value={creds.password}
              onChange={(e) => setCreds({ ...creds, password: e.target.value })}
              onKeyDown={(e) => e.key === 'Enter' && doLogin()}
              className="w-full bg-black border-2 border-gray-800 px-3 py-3 text-white font-mono text-sm focus:border-orange-500 outline-none"
              data-testid="admin-hub-password"
            />
            <button
              onClick={doLogin}
              disabled={loggingIn}
              className="w-full bg-orange-500 hover:bg-orange-400 disabled:bg-gray-800 text-black py-4 font-black text-xs tracking-[0.25em] uppercase transition-colors flex items-center justify-center gap-2"
              data-testid="admin-hub-login-btn"
            >
              {loggingIn ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Sign In'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="admin-hub">
      <EnvBanner />
      <div className="py-14 px-4">
      <div className="mx-auto w-full max-w-5xl">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 border-b border-gray-800 pb-6">
          <div>
            <div className="text-[10px] font-bold tracking-[0.4em] text-orange-500 uppercase flex items-center gap-2">
              <ShieldCheck className="w-3 h-3" /> Authenticated
            </div>
            <h1 className="mt-2 text-3xl sm:text-4xl font-black uppercase tracking-tight">Admin Hub</h1>
            <p className="text-xs text-gray-500 mt-2">Every console, one door.</p>
          </div>
          <button
            onClick={logout}
            className="flex items-center gap-2 px-4 py-2 border-2 border-gray-800 text-gray-400 hover:text-white hover:border-orange-500 transition-colors text-[10px] font-bold uppercase tracking-[0.2em]"
            data-testid="admin-hub-logout"
          >
            <LogOut className="w-3.5 h-3.5" /> Sign Out
          </button>
        </div>

        {/* Quick tool: comp free L'Chaim access by email */}
        <GrantLchaimTool />

        {/* Quick tool: recent account activity (sign-ups + last-active) */}
        <RecentActivityTool />


        {/* Quick tool: send the parent welcome + orientation email */}
        <ParentWelcomeTool />

        {/* Quick tool: send a passwordless family progress (Family Hub) link */}
        <ParentLinkTool />

        {/* Quick tool: view family orientation bookings */}
        <OrientationBookingsTool />

        {/* Quick tool: host a live video session to students */}
        <LiveSessionTool />

        {/* Quick tool: generate today's AI Head Coach briefing videos */}
        <CoachBriefingTool />

        {/* Sections */}
        <div className="mt-10 space-y-12">
          {SECTIONS.map((section) => (
            <div key={section.group}>
              <div className="text-[10px] font-bold tracking-[0.3em] text-gray-500 uppercase mb-4">{section.group}</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {section.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className="group relative border-2 border-gray-800 bg-[#0a0a0a] p-5 hover:border-orange-500 transition-colors"
                      data-testid={`admin-hub-card-${item.path.replace(/\//g, '-').replace(/^-/, '')}`}
                    >
                      <ArrowUpRight className="absolute top-4 right-4 w-4 h-4 text-gray-700 group-hover:text-orange-500 transition-colors" />
                      <div className="w-10 h-10 flex items-center justify-center border-2 border-gray-800 group-hover:border-orange-500 group-hover:bg-orange-500 transition-colors">
                        <Icon className="w-5 h-5 text-orange-500 group-hover:text-black transition-colors" />
                      </div>
                      <h3 className="mt-4 font-black text-base uppercase tracking-tight leading-tight">{item.title}</h3>
                      <div className="text-[10px] font-mono text-gray-600 mt-1">{item.path}</div>
                      <p className="text-xs text-gray-500 mt-3 leading-relaxed">{item.desc}</p>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
      </div>
    </div>
  );
};

export default AdminHub;
